import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getUser } from '../../../store/slices/userSlice';
import { getJobs } from '../../../store/slices/jobsSlice';
import { getUsersList } from '../../../store/slices/usersSlice';
import { formatUserDisplayName, resolveUserDisplayNameByEmail } from '../../../utils/userDisplayName';
import JobDetailsDialog from '../jobs/JobDetailsDialog';
import RequestsSection from './RequestsSection';
import RequestsActionButtons from './RequestsActionButtons';
import {
  DEFAULT_GRID_PREFERENCE,
  DEFAULT_SECTION_HEIGHT,
  GRID_PREFERENCES_STORAGE_KEY,
  SECTION_HEIGHTS_STORAGE_KEY,
  clampSectionHeight,
  parseGridPreferences,
  parseSectionHeights,
  sanitizeGridPreference,
} from './RemindersColumnsHelper';
import { deriveUnassignedPendingJobRows } from '../../../utils/jobTechnicianRequests';
import { routes } from '../../../utils/constants';
import { canAccessPendingJobsTab } from '../../../utils/permissions';
import { getStatusLabel, getStatusChipStyles } from '../../../utils/jobStatuses';
import { getPriorityLabel } from '../../../utils/jobPriorities';
import { getClientChipStyles } from '../../../utils/clientColors';
import useIsMobile from '../../../hooks/useIsMobile';

const Reminders = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const user = useSelector(getUser);
  const { jobs } = useSelector(getJobs);
  const usersList = useSelector(getUsersList);

  const displayUserByEmail = useMemo(() => {
    const byEmail = new Map(
      (usersList || [])
        .filter((u) => u.email)
        .map((u) => [u.email.toLowerCase(), u])
    );
    return (email) => {
      if (!email) return '';
      const match = byEmail.get(String(email).toLowerCase());
      return match ? formatUserDisplayName(match) : email;
    };
  }, [usersList]);

  const [pendingJobsSearchQuery, setPendingJobsSearchQuery] = useState('');
  const [selectedJobForDetails, setSelectedJobForDetails] = useState(null);
  const [initialTabValue, setInitialTabValue] = useState(1);

  const loadExpandedSections = () => {
    try {
      const saved = localStorage.getItem('remindersSectionsExpanded');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          pendingJobs: parsed.pendingJobs !== undefined ? parsed.pendingJobs : true,
        };
      }

      // Migrate expanded state from the old Requests page storage key
      const legacySaved = localStorage.getItem('requestsSectionsExpanded');
      if (legacySaved) {
        const legacy = JSON.parse(legacySaved);
        if (legacy.pendingJobs !== undefined) {
          return { pendingJobs: legacy.pendingJobs };
        }
      }
    } catch (error) {
      console.error('Error loading expanded sections from localStorage:', error);
    }
    return { pendingJobs: true };
  };

  const [expandedSections, setExpandedSections] = useState(loadExpandedSections);
  const [gridPreferences, setGridPreferences] = useState(() => parseGridPreferences());
  const [sectionHeights, setSectionHeights] = useState(() => parseSectionHeights());
  const [fullscreenSection, setFullscreenSection] = useState(null);

  const canAccessReminders = canAccessPendingJobsTab(user);

  const remindersChipSx = (bgColor, textColor, borderColor) => ({
    backgroundColor: bgColor,
    color: textColor,
    fontSize: '0.75rem',
    fontWeight: 500,
    height: '24px',
    borderRadius: '6px',
    border: `1px solid ${borderColor}`,
    '& .MuiChip-label': {
      padding: '0 8px',
    },
  });

  const renderRemindersClientChip = (params) => {
    const clientId = params.row.client_id;
    const label = params.value || 'Unknown';
    const { bgColor, textColor, borderColor } = getClientChipStyles(clientId);
    return (
      <Chip
        label={label}
        sx={remindersChipSx(bgColor, textColor, borderColor)}
      />
    );
  };

  const renderRemindersJobStatusChip = (params) => {
    const statusId = Number(params.value);
    const label = getStatusLabel(statusId);
    const { bgColor, textColor, borderColor } = getStatusChipStyles(statusId);
    return (
      <Chip
        label={label}
        sx={remindersChipSx(bgColor, textColor, borderColor)}
      />
    );
  };

  const renderRemindersJobPriorityChip = (params) => {
    const priority = params.value;
    const label = getPriorityLabel(priority);
    const p = Number(priority);
    let bgColor;
    let textColor;
    let borderColor;
    if (p === 1) {
      bgColor = isDarkMode ? '#7f1d1d' : '#fee2e2';
      textColor = isDarkMode ? '#fca5a5' : '#991b1b';
      borderColor = '#ef4444';
    } else if (p === 2) {
      bgColor = isDarkMode ? '#78350f' : '#fef3c7';
      textColor = isDarkMode ? '#fbbf24' : '#92400e';
      borderColor = '#f59e0b';
    } else if (p === 3) {
      bgColor = isDarkMode ? '#1e3a8a' : '#dbeafe';
      textColor = isDarkMode ? '#93c5fd' : '#1e3a8a';
      borderColor = '#3b82f6';
    } else if (p === 4) {
      bgColor = isDarkMode ? '#374151' : '#e5e7eb';
      textColor = isDarkMode ? '#d1d5db' : '#374151';
      borderColor = '#6b7280';
    } else {
      bgColor = isDarkMode ? '#374151' : '#e5e7eb';
      textColor = isDarkMode ? '#d1d5db' : '#374151';
      borderColor = '#6b7280';
    }
    return (
      <Chip
        label={label}
        sx={remindersChipSx(bgColor, textColor, borderColor)}
      />
    );
  };

  const handleOpenPendingJobOverlay = (row) => {
    if (!row?.job_id) return;
    const job = (jobs || []).find(j => j.id === row.job_id);
    if (job) {
      setInitialTabValue(1);
      setSelectedJobForDetails(job);
    }
  };

  const handleNavigateToPendingJob = (row) => {
    if (!row?.job_id) return;
    navigate(routes.jobs, { state: { openJobId: row.job_id, openJobTab: 'technicians' } });
  };

  const handlePendingJobRowClick = (row) => {
    handleOpenPendingJobOverlay(row);
  };

  const pendingJobsColumns = [
    { field: 'work_order', headerName: 'Work Order', width: 140 },
    {
      field: 'client_name',
      headerName: 'Client',
      width: 180,
      renderCell: renderRemindersClientChip,
    },
    { field: 'description', headerName: 'Description', width: 200 },
    {
      field: 'dispatcher',
      headerName: 'Dispatcher',
      width: 160,
      valueFormatter: (value) => displayUserByEmail(value) || 'Not assigned',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: renderRemindersJobStatusChip,
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 110,
      renderCell: renderRemindersJobPriorityChip,
    },
    { field: 'trade', headerName: 'Trade', width: 120 },
    { field: 'technician_count', headerName: '# Techs', width: 90 },
    { field: 'city', headerName: 'City', width: 120 },
    { field: 'state', headerName: 'State', width: 80 },
    {
      field: 'nte',
      headerName: 'NTE',
      width: 100,
      valueFormatter: value => value ? `$${Number(value).toLocaleString()}` : '',
    },
    {
      field: 'attention_reasons_text',
      headerName: 'Reason(s)',
      width: 250,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 110,
      valueFormatter: value => value ? String(value).slice(0, 10) : '',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <RequestsActionButtons
          onOpenHere={() => handleOpenPendingJobOverlay(params.row)}
          onOpenInJobs={() => handleNavigateToPendingJob(params.row)}
        />
      ),
    },
  ];

  const pendingJobRows = useMemo(() => {
    return deriveUnassignedPendingJobRows(jobs || []);
  }, [jobs]);

  const filteredPendingJobRows = useMemo(() => {
    if (!pendingJobsSearchQuery) return pendingJobRows;
    const query = pendingJobsSearchQuery.toLowerCase();
    return pendingJobRows.filter(row =>
      row.work_order?.toLowerCase().includes(query) ||
      row.client_name?.toLowerCase().includes(query) ||
      row.description?.toLowerCase().includes(query) ||
      row.status_label?.toLowerCase().includes(query) ||
      row.priority_label?.toLowerCase().includes(query) ||
      String(row.status ?? '').toLowerCase().includes(query) ||
      String(row.priority ?? '').toLowerCase().includes(query) ||
      row.trade?.toLowerCase().includes(query) ||
      row.city?.toLowerCase().includes(query) ||
      row.attention_reasons_text?.toLowerCase().includes(query) ||
      row.dispatcher?.toLowerCase().includes(query) ||
      resolveUserDisplayNameByEmail(row.dispatcher, usersList).toLowerCase().includes(query) ||
      (!row.dispatcher && 'not assigned'.includes(query))
    );
  }, [pendingJobRows, pendingJobsSearchQuery, usersList]);

  const handleSectionChange = (section) => (event, isExpanded) => {
    setExpandedSections(prev => {
      const updated = { ...prev, [section]: isExpanded };
      try {
        localStorage.setItem('remindersSectionsExpanded', JSON.stringify(updated));
      } catch (error) {
        console.error('Error saving expanded sections to localStorage:', error);
      }
      return updated;
    });
  };

  const sectionColumnFields = useMemo(() => ({
    pendingJobs: pendingJobsColumns.map((column) => column.field),
  }), [pendingJobsColumns.map((column) => column.field).join(',')]);

  const sanitizedGridPreferences = useMemo(() => (
    Object.entries(sectionColumnFields).reduce((acc, [sectionKey, fields]) => {
      if (!fields.length) return acc;
      acc[sectionKey] = sanitizeGridPreference(
        gridPreferences?.[sectionKey] ?? DEFAULT_GRID_PREFERENCE,
        fields,
      );
      return acc;
    }, {})
  ), [gridPreferences, sectionColumnFields]);

  useEffect(() => {
    window.localStorage.setItem(
      GRID_PREFERENCES_STORAGE_KEY,
      JSON.stringify(sanitizedGridPreferences),
    );
  }, [sanitizedGridPreferences]);

  useEffect(() => {
    window.localStorage.setItem(SECTION_HEIGHTS_STORAGE_KEY, JSON.stringify(sectionHeights));
  }, [sectionHeights]);

  const updateSectionGridPreference = useCallback((sectionKey, updater) => {
    setGridPreferences((prev) => {
      const fields = sectionColumnFields[sectionKey] || [];
      const currentPreference = sanitizeGridPreference(
        prev?.[sectionKey] ?? DEFAULT_GRID_PREFERENCE,
        fields,
      );
      const nextPreference = typeof updater === 'function' ? updater(currentPreference) : updater;
      return {
        ...prev,
        [sectionKey]: sanitizeGridPreference(nextPreference, fields),
      };
    });
  }, [sectionColumnFields]);

  const handleSectionHeightChange = useCallback((sectionKey, nextHeight) => {
    setSectionHeights((prev) => ({
      ...prev,
      [sectionKey]: clampSectionHeight(nextHeight),
    }));
  }, []);

  const handleToggleFullscreen = useCallback((sectionKey) => {
    setFullscreenSection((prev) => (prev === sectionKey ? null : sectionKey));
  }, []);

  const searchFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      backgroundColor: 'background.paper',
      fontSize: '0.875rem',
      height: '36px',
      '& input::placeholder': {
        color: 'text.secondary',
        opacity: 1,
      },
      '& fieldset': {
        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: isDarkMode ? '#475569' : '#e0e0e0',
      },
      '&.Mui-focused fieldset': {
        borderColor: isDarkMode ? '#475569' : '#e0e0e0',
        borderWidth: '1px',
      },
    }
  };

  if (!canAccessReminders) {
    return (
      <Box sx={{
        color: 'text.primary',
        bgcolor: 'background.paper',
        padding: '24px',
        borderRadius: '8px',
        margin: '40px auto',
        maxWidth: '400px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '1.2rem',
        boxShadow: isDarkMode ? '0 2px 8px rgba(211,47,47,0.3)' : '0 2px 8px rgba(211,47,47,0.15)'
      }}>
        You are not authorized to view this page
      </Box>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          width: '100%',
          padding: { xs: 1, md: 3 },
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.25rem', md: '2rem' },
              color: 'text.primary',
            }}
          >
            Reminders
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flex: 1,
            minHeight: 0,
            overflowY: fullscreenSection ? 'hidden' : 'auto',
          }}
        >
          <RequestsSection
            sectionKey="pendingJobs"
            title="Unassigned/Pending Jobs"
            rows={filteredPendingJobRows || []}
            columns={pendingJobsColumns}
            searchPlaceholder="Search by work order, client, status..."
            searchQuery={pendingJobsSearchQuery}
            onSearchChange={setPendingJobsSearchQuery}
            searchFieldSx={searchFieldSx}
            expanded={expandedSections.pendingJobs}
            onExpandedChange={handleSectionChange('pendingJobs')}
            height={sectionHeights.pendingJobs ?? DEFAULT_SECTION_HEIGHT}
            onHeightChange={(nextHeight) => handleSectionHeightChange('pendingJobs', nextHeight)}
            isFullscreen={fullscreenSection === 'pendingJobs'}
            onToggleFullscreen={handleToggleFullscreen}
            hidden={Boolean(fullscreenSection && fullscreenSection !== 'pendingJobs')}
            gridPreference={sanitizedGridPreferences.pendingJobs}
            onUpdateGridPreference={(updater) => updateSectionGridPreference('pendingJobs', updater)}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            onRowClick={(params) => handlePendingJobRowClick(params.row)}
          />
        </Box>
      </Paper>

      <JobDetailsDialog
        selectedJob={selectedJobForDetails}
        setSelectedJob={setSelectedJobForDetails}
        user={user}
        initialTabValue={initialTabValue}
      />
    </>
  );
};

export default Reminders;
